import { colors as themeColors } from '../../../utils/theme';
import React, { useEffect, useState, useRef } from 'react';
import { Box, Grid, Paper, Typography, TextField, Button, Dialog, DialogContent, Table, TableHead, TableRow, TableCell, TableBody, TableFooter, Snackbar, Alert } from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import TransportMaster from './TransportMaster';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { toProperCase } from '../../../utils/formatRows';

const formatDate = (date) => {
    if (!date) return '';
    if (dayjs.isDayjs(date)) return date.format('DD/MM/YYYY');
    const d = new Date(date);
    return isNaN(d) ? '' : d.toISOString().split('T')[0];
};

const parseDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return null;

    const parsed = dayjs(dateStr, ['DD/MM/YYYY', 'YYYY-MM-DD']);
    return parsed.isValid() ? parsed : null;
};

const TransporterDetails = ({ initialData = {}, onChange, connectionString }) => {
    const { user, selectedCompany, loading } = useAuth();
    const userId = user?.UserId || null;
    const companyId = selectedCompany?.CompId || null;
    const [transporterName, setTransporterName] = useState(initialData?.transporterName || '');
    const [transportId, setTransportId] = useState(initialData?.transportId || null);
    const [gstNo, setGstNo] = useState(initialData?.gstNo || '');
    const [vehicleNumber, setVehicleNumber] = useState(initialData?.vehicleNumber || '');
    const [dispatchFrom, setDispatchFrom] = useState(initialData?.dispatchFrom || '');
    const [dispatchTo, setDispatchTo] = useState(initialData?.dispatchTo || '');
    const [docThrough, setDocThrough] = useState(initialData?.docThrough || '');
    const [lrNumber, setLrNumber] = useState(initialData?.lrNumber || '');
    const [lrDate, setLrDate] = useState(parseDate(initialData?.lrDate) || null);
    const [totalPkg, setTotalPkg] = useState(initialData?.totalPkg || '');
    const [ewayBillNumber, setEwayBillNumber] = useState(initialData?.ewayBillNumber || '');
    const [ewayBillDate, setEwayBillDate] = useState(formatDate(initialData?.ewayBillDate) || '');
    const [irnNumber, setIrnNumber] = useState(initialData?.irnNumber || '');
    const [irnDate, setIrnDate] = useState(formatDate(initialData?.irnDate) || '');
    const [ackNumber, setAckNumber] = useState(initialData?.ackNumber || '');
    const [ackDate, setAckDate] = useState(formatDate(initialData?.ackDate) || '');
    const [suggestions, setSuggestions] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [error, setError] = useState('');
    const dropdownRef = useRef(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const requiredAsteriskStyle = { '& .MuiFormLabel-asterisk': { color: 'red', }, };

    const normalizeBase = (s) => {
        if (!s) return '';
        // ensure protocol exists and no trailing slash
        let base = String(s).trim();
        // if user passed no protocol, try adding http:// (only for quick test; prefer full URL)
        if (!/^https?:\/\//i.test(base)) base = 'http://' + base;
        return base.replace(/\/+$/, '');
    };

    const fetchTransporterSuggestions = async (name) => {
        console.log('>>> fetchTransporterSuggestions called', { name, companyId, connectionString });

        // Basic guards
        if (!connectionString) {
            console.error('fetchTransporterSuggestions: connectionString is falsy. Cannot call API.');
            return;
        }
        if (!name) {
            console.warn('fetchTransporterSuggestions: name too short; skipping request.');
            setSuggestions([]);
            return;
        }
        if (!companyId) {
            console.warn('fetchTransporterSuggestions: companyId is falsy. Skipping request until company is available.');
            setSuggestions([]);
            return;
        }

        const base = normalizeBase(connectionString);
        const url = `${base}/api/transport/search`;
        const params = { mode: 'search', TransporterName: name, companyId };

        console.log('Will call transporter search', { url, params });

        try {
            // log time so we can correlate in backend
            const start = Date.now();

            const res = await axios.get(url, { params, headers: { 'Cache-Control': 'no-store' }, timeout: 15000, });

            console.log('Transporter API success', { url, params, status: res.status, durationMs: Date.now() - start, dataSample: Array.isArray(res.data) ? res.data.slice(0, 3) : res.data });

            // adapt response shape
            const data = Array.isArray(res.data) ? res.data : (res.data.records || res.data.data || []);
            setSuggestions(Array.isArray(data) ? data : []);
        } catch (err) {
            // Detailed error logging to help you identify the blocker
            console.error('Transporter API request failed', {
                message: err.message,
                isAxiosError: !!err.isAxiosError,
                code: err.code,
                configUrl: err?.config?.url,
                configParams: err?.config?.params,
                responseStatus: err?.response?.status,
                responseData: err?.response?.data,
                requestObj: err?.request // this exists when request was sent but no response
            });

            // Distinguish common browser-block cases:
            // - Mixed content: browser console shows "Mixed Content" (can't be caught in JS); check console.
            // - CORS preflight failure: you'll usually see a CORS error in console and err.request populated.
            // - Network error: err.request exists, err.response is undefined.
            setSuggestions([]);
        }
    };

    // ---------- Test button helper: triggers call regardless of dropdown or input ----------
    const testApiCall = async () => {
        const testName = transporterName?.trim() || 'abc'; // you can change to a valid search term
        console.log('Manual testApiCall invoked', { testName, connectionString, companyId });

        if (!connectionString) {
            console.error('Please provide connectionString (include protocol and port).');
            return;
        }
        if (!companyId) {
            console.error('companyId not available yet — cannot test.');
            return;
        }

        // call fetcher (it will log output)
        await fetchTransporterSuggestions(testName);

        // Also try a raw fetch (this will show up in Network tab as a plain fetch - helps debug axios-specific issues)
        try {
            const base = normalizeBase(connectionString);
            const rawUrl = `${base}/api/transport/search?mode=search&TransporterName=${encodeURIComponent(testName)}&companyId=${encodeURIComponent(companyId)}`;
            console.log('Raw fetch test ->', rawUrl);
            const rawRes = await fetch(rawUrl, { method: 'GET', mode: 'cors' });
            console.log('Raw fetch status:', rawRes.status, rawRes.type);
            // attempt to parse json if allowed
            const txt = await rawRes.text();
            console.log('Raw fetch response text (truncated):', txt.slice(0, 200));
        } catch (rawErr) {
            console.error('Raw fetch failed', rawErr);
        }
    };

    const handlePopupClose = (data) => {
        setShowPopup(false)

        if (data?.success) {
            setSnackbar({ open: true, message: data.message, severity: "success" });
        }
    };

    // useEffect to call the function when conditions are met
    useEffect(() => {
        if (transporterName && dropdownOpen) {
            fetchTransporterSuggestions(transporterName);
        }
    }, [transporterName, dropdownOpen]);

    // Close dropdown if clicked outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelectTransporter = (transporter) => {
        setTransporterName(transporter.TransporterName);
        setTransportId(transporter.TransPortID);
        setGstNo(transporter.GSTNO);
        setDropdownOpen(false);
        setSuggestions([]);
    };

    // const handleNext = () => {
    //     if (!transporterName) {
    //         setError('Transporter Name is required.');
    //         return;
    //     }
    //     setError('');
    //     onNext({ transportId, transporterName, gstNo, vehicleNumber, dispatchFrom, dispatchTo, docThrough, lrNumber, lrDate: lrDate ? lrDate.format('DD/MM/YYYY') : '', totalPkg, ewayBillNumber, ewayBillDate, irnNumber, irnDate, ackNumber, ackDate, });
    // };

    useEffect(() => {
        if (!transporterName) return;

        onChange?.({
            transportId,
            transporterName,
            gstNo,
            vehicleNumber,
            dispatchFrom,
            dispatchTo,
            docThrough,
            lrNumber,
            lrDate: lrDate ? lrDate.format('DD/MM/YYYY') : '',
            totalPkg,
            ewayBillNumber,
            ewayBillDate,
            irnNumber,
            irnDate,
            ackNumber,
            ackDate,
        });
    }, [
        transportId,
        transporterName,
        gstNo,
        vehicleNumber,
        dispatchFrom,
        dispatchTo,
        docThrough,
        lrNumber,
        lrDate,
        totalPkg,
        ewayBillNumber,
        ewayBillDate,
        irnNumber,
        irnDate,
        ackNumber,
        ackDate
    ]);


    return (
        <Paper elevation={2} sx={{ p: 2, position: 'relative', mt: -1.5 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} >
                    {/* Left Column */}
                    <Box flex={{ xs: '1 1 100%', md: '0 0 59%' }} sx={{ width: { xs: '100%', md: '59%' }, }} >
                        <Box display='flex' gap={1}>
                            <Box mb={2} flex={1} sx={{ position: 'relative' }}>
                                <TextField fullWidth size='small' label="Transporter Name" value={toProperCase(transporterName)} onClick={() => setDropdownOpen(true)}
                                    onChange={(e) => {
                                        setTransporterName(toProperCase(e.target.value));
                                        setDropdownOpen(true);
                                    }}
                                    InputLabelProps={{ sx: requiredAsteriskStyle }}
                                    sx={{ '& .MuiInputBase-root': { height: 38, } }}
                                />
                                {/* {dropdownOpen && transporterName && (
                                    <Paper ref={dropdownRef} sx={{ position: 'absolute', zIndex: 10, maxHeight: 250, overflowY: 'auto', mt: 1, width: '100%', }} >
                                        {suggestions.length > 0 ? (
                                            suggestions.map((item) => (
                                                <Box key={item.id} sx={{ px: 2, py: 1, cursor: 'pointer', '&:hover': { backgroundColor: '#eee' }, }} onClick={() => handleSelectTransporter(item)} >
                                                    <strong>{item.TransporterName}</strong> — {item.GSTNO}
                                                </Box>
                                            ))
                                        ) : (
                                            <Box sx={{ px: 2, py: 1 }}>No results found</Box>
                                        )}
                                        <Box sx={{ textAlign: 'right', p: 1 }}>
                                            <Button size="small" onClick={() => setShowPopup(true)}> + Add New </Button>
                                        </Box>
                                    </Paper>
                                )} */}
                                {dropdownOpen && transporterName && (
                                    <Paper
                                        ref={dropdownRef}
                                        elevation={0}
                                        sx={{
                                            mt: 1,
                                            position: 'absolute',
                                            width: { xs: '200%', sm: '120%' },
                                            zIndex: 10,
                                            border: '1px solid rgba(0,0,0,0.2)',
                                            borderRadius: 1,
                                            maxHeight: 240,
                                            overflowY: 'auto',
                                            backgroundColor: themeColors.neutral.surface,
                                        }}
                                    >
                                        <Table size="small" stickyHeader>
                                            {/* ================= HEADER ================= */}
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell
                                                        sx={{
                                                            backgroundColor: themeColors.neutral.line,
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            position: 'sticky',
                                                            top: 0,
                                                            zIndex: 1,
                                                        }}
                                                    >
                                                        Transporter Name
                                                    </TableCell>

                                                    <TableCell
                                                        sx={{
                                                            backgroundColor: themeColors.neutral.line,
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            position: 'sticky',
                                                            top: 0,
                                                            zIndex: 1,
                                                            width: '140px'
                                                        }}
                                                    >
                                                        GST No
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>

                                            {/* ================= BODY ================= */}
                                            <TableBody>
                                                {suggestions.length > 0 ? (
                                                    suggestions.map((item, i) => (
                                                        <TableRow
                                                            key={i}
                                                            hover
                                                            sx={{ cursor: 'pointer' }}
                                                            onMouseDown={() => {
                                                                handleSelectTransporter(item);
                                                                setDropdownOpen(false);
                                                            }}
                                                        >
                                                            <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500, wordWrap: 'normal', wordBreak: 'break-word' }}>
                                                                {item.TransporterName}
                                                            </TableCell>

                                                            <TableCell sx={{ fontSize: '0.8rem' }}>
                                                                {item.GSTNO || '-'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={2} align="center" sx={{ color: 'error.main' }}>
                                                            Transporter not found
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>

                                            {/* ================= FOOTER ================= */}
                                            <TableFooter
                                                sx={{
                                                    position: 'sticky',
                                                    bottom: 0,
                                                    backgroundColor: themeColors.neutral.surface,
                                                }}
                                            >
                                                <TableRow hover onMouseDown={() => setShowPopup(true)}>
                                                    <TableCell
                                                        colSpan={2}
                                                        align="center"
                                                        sx={{
                                                            color: 'primary.main',
                                                            fontWeight: 600,
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        ➕ Add New Transporter
                                                    </TableCell>
                                                </TableRow>
                                            </TableFooter>
                                        </Table>
                                    </Paper>
                                )}
                            </Box>
                            <Box mb={2} flex={1}>
                                <TextField fullWidth size='small' label="GST No." value={gstNo} disabled sx={{ '& .MuiInputBase-root': { height: 38, } }} />
                            </Box>
                        </Box>

                        <Box display="flex" gap={1}>
                            {/* Left Half: Vehicle No. + Total Pkg. (each 50% of this half) */}
                            <Box display="flex" gap={1} flex={1}>
                                <Box flex={1}>
                                    <TextField fullWidth size="small" label="Vehicle No." value={vehicleNumber} onChange={(e) => setVehicleNumber(e.target.value)} onBlur={() => {
                                        const cleaned = vehicleNumber.replace(/[\s\-\/\.!@#$%^&*()_+=\[\]{};:'",<>?\\|`~]/g, '');
                                        setVehicleNumber(cleaned.toUpperCase());
                                    }} sx={{ '& .MuiInputBase-root': { height: 38, }, }} />
                                </Box>
                                <Box flex={1}>
                                    <TextField fullWidth size="small" label="Total Pkg." type="number" value={totalPkg} onChange={(e) => setTotalPkg(e.target.value)} sx={{ '& .MuiInputBase-root': { height: 38, }, }} />
                                </Box>
                            </Box>

                            {/* Right Half: Doc Through */}
                            <Box flex={1} mb={2}>
                                <TextField fullWidth size="small" label="Doc Through" value={toProperCase(docThrough)} onChange={(e) => setDocThrough(toProperCase(e.target.value))} sx={{ '& .MuiInputBase-root': { height: 38, }, }} />
                            </Box>
                        </Box>

                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {/* Disp. From */}
                            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 4px)', md: 'calc(25% - 6px)', }, }} >
                                <TextField fullWidth size="small" label="Disp. From" value={dispatchFrom} onChange={(e) => setDispatchFrom(e.target.value)} sx={{ '& .MuiInputBase-root': { height: 38, }, }} />
                            </Box>

                            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 4px)', md: 'calc(25% - 6px)', }, }} >
                                <TextField fullWidth size="small" label="Disp. To" value={dispatchTo} onChange={(e) => setDispatchTo(e.target.value)} sx={{ '& .MuiInputBase-root': { height: 38, }, }} />
                            </Box>

                            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 4px)', md: 'calc(25% - 6px)', }, }} >
                                <TextField fullWidth size="small" label="LR No." value={lrNumber} onChange={(e) => setLrNumber(e.target.value)} sx={{ '& .MuiInputBase-root': { height: 38, }, }} />
                            </Box>

                            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 4px)', md: 'calc(25% - 6px)', }, mb: 2 }} >
                                <DatePicker label="LR Date" value={lrDate} onChange={(date) => setLrDate(date ? date : null)} format="DD/MM/YYYY"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true, size: 'small',
                                            sx: {
                                                '& .MuiInputBase-root': { height: 38, alignItems: 'center', },
                                                '& .MuiOutlinedInput-root': { paddingRight: '40px', },
                                            },
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>

                    {/* Right Column */}
                    <Box flex={{ xs: '1 1 100%', md: '0 0 39%' }} sx={{ width: { xs: '100%', md: '39%' }, }} pl={{ md: 1 }} >
                        {/* Row 1 */}
                        {/* <Box display="flex" gap={1}>
                            <Box mb={2} flex={1} minWidth={0}>
                                <TextField fullWidth size="small" disabled label="E-Way Bill No." value={ewayBillNumber} onChange={(e) => setEwayBillNumber(e.target.value)} sx={{ '& .MuiInputBase-root': { height: 40, }, }} />
                            </Box>
                            <Box mb={2} flex={1} minWidth={0}>
                                <DatePicker label="E-Way Bill Date" disabled value={parseDate(ewayBillDate)} onChange={(date) => setEwayBillDate(date ? date.format('DD/MM/YYYY') : '')} format="DD/MM/YYYY"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true, size: 'small',
                                            sx: {
                                                '& .MuiInputBase-root': { minHeight: 40, alignItems: 'center', },
                                                '& .MuiOutlinedInput-root': { paddingRight: '40px', },
                                            },
                                        },
                                    }}
                                />
                            </Box>
                        </Box> */}

                        {/* Row...

                        {/* Row 2 */}
                        {/* <Box display="flex" gap={1}>
                            <Box mb={2} flex={1} minWidth={0}>
                                <TextField fullWidth size="small" disabled label="IRN No." value={irnNumber} onChange={(e) => setIrnNumber(e.target.value)} sx={{ '& .MuiInputBase-root': { height: 40, }, }} />
                            </Box>
                            <Box mb={2} flex={1} minWidth={0}>
                                <DatePicker label="IRN Date" disabled value={parseDate(irnDate)} onChange={(date) => setIrnDate(date ? date.format('DD/MM/YYYY') : '')} format="DD/MM/YYYY"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true, size: 'small',
                                            sx: {
                                                '& .MuiInputBase-root': { minHeight: 40, alignItems: 'center', },
                                                '& .MuiOutlinedInput-root': { paddingRight: '40px', },
                                            },
                                        },
                                    }}
                                />
                            </Box>
                        </Box> */}

                        {/* Row 3 */}
                        {/* <Box display="flex" gap={1}>
                            <Box mb={2} flex={1} minWidth={0}>
                                <TextField fullWidth size="small" disabled label="Ack No." value={ackNumber} onChange={(e) => setAckNumber(e.target.value)} sx={{ '& .MuiInputBase-root': { height: 40, }, }} />
                            </Box>
                            <Box mb={2} flex={1} minWidth={0}>
                                <DatePicker label="Ack Date" disabled value={parseDate(ackDate)} onChange={(date) => setAckDate(date ? date.format('DD/MM/YYYY') : '')} format="DD/MM/YYYY"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true, size: 'small',
                                            sx: {
                                                '& .MuiInputBase-root': { minHeight: 40, alignItems: 'center', },
                                                '& .MuiOutlinedInput-root': { paddingRight: '40px', },
                                            },
                                        },
                                    }}
                                />
                            </Box>
                        </Box> */}
                    </Box>
                </Box>
            </LocalizationProvider>

            {error && (
                <Typography color="error" mt={2}>
                    {error}
                </Typography>
            )}

            {/* <Box mt={4} display="flex" justifyContent="flex-end">
                <Button variant="contained" onClick={handleNext}>
                    Next
                </Button>
            </Box> */}

            <Dialog open={showPopup} onClose={(event, reason) => {
                if (reason === 'backdropClick') return;
                setShowPopup(false);
            }} maxWidth="sm">
                <DialogContent>
                    <TransportMaster mode="add" onPopupCancel={handlePopupClose} connectionString={connectionString} />
                </DialogContent>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={2000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} >
                <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default TransporterDetails;